import type {
  FlowExportResponse,
  Health,
  LexBot,
  LexBotDetail,
  MigrationRequest,
  MigrationResult,
  Planner,
} from "../types";

/**
 * The only module that knows the backend exists.
 *
 * Relative URLs throughout: in development Vite proxies /api to the backend,
 * and in production the backend serves this bundle itself. Either way the
 * browser sees one origin, so there is no base URL to configure and no CORS
 * to negotiate.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    // fetch only rejects on a transport failure, so this is genuinely "the
    // server is not there" rather than any error it reported.
    throw new ApiError("Cannot reach the server. Is the backend running?", 0);
  }

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      (body as { error?: string })?.error ??
      `Request failed with ${response.status}`;
    throw new ApiError(message, response.status);
  }
  return body as T;
}

export const api = {
  health: () => request<Health>("/api/health"),

  bots: () =>
    request<{ ok: boolean; bots: LexBot[] }>("/api/bots").then((r) => r.bots),

  /** One bot's intents and slots, read before a migration rather than after. */
  bot: (botId: string) =>
    request<{ ok: boolean; bot: LexBotDetail }>(
      `/api/bots/${encodeURIComponent(botId)}`,
    ).then((r) => r.bot),

  /**
   * The created flows, as the service stored them.
   *
   * `folder` lets the server answer from the copy saved at migration time,
   * which survives the flow being deleted; without it the server reads live.
   */
  flows: (flowIds: string[], folder?: string) =>
    request<FlowExportResponse>(
      `/api/flows?ids=${encodeURIComponent(flowIds.join(","))}` +
        (folder ? `&folder=${encodeURIComponent(folder)}` : ""),
    ),

  /**
   * The planner selects the ROUTE, not a field in the body. The backend
   * exposes the two separately so the choice is deliberate, and the route is
   * what decides - a body field could not override it.
   */
  migrate: (planner: Planner, body: MigrationRequest) =>
    request<MigrationResult>(`/api/migrate/${planner}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
};
